import { useCallback, useLayoutEffect, useMemo } from "react";
import { useRouter } from "expo-router";
import { useSites } from "@src/features/cameras/hooks/useSites";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { useCameras } from "@src/features/cameras";
import {
  useAccessManagement,
  useSetUserPermissions,
  useRevokeUserAccess,
  useIsTenantAdmin,
  isLegacyCamera,
} from "@visioni/a3s-access/app";
import type {
  EditUserFormData,
  SiteAccessListSite,
  CameraAccessListCamera,
  SitePermission,
  CameraPermission,
} from "@visioni/a3s-access/app";
import type { Role } from "@visioni/a3s-access/schemas";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { useSelfActionGuard } from "./useSelfActionGuard";
import { universalConfirm } from "@src/components/UniversalAlert";
import { withLegacyCameraFallbacks } from "@src/features/settings/utils";
import { formatCameraLabel } from "@src/utils/key-parser";
import { normalizeCameraNickname } from "@src/features/cameras/utils/camera-display-name";

export function useEditUserPage(userId: string) {
  const router = useRouter();
  const {
    isTenantAdmin: isCurrentUserAdmin,
    isLoading: permissionsLoading,
    isError: permissionsError,
  } = useIsTenantAdmin();
  const { sites, isLoading: sitesLoading } = useSites();
  const siteNames = useSiteNames();
  const { data: cameras, isLoading: camerasLoading } = useCameras();
  const { data: accessData, isLoading: accessLoading } = useAccessManagement();
  const setUserPermissions = useSetUserPermissions();
  const revokeUserAccess = useRevokeUserAccess();
  const { isSelf, guard: selfGuard } = useSelfActionGuard(userId);

  const user = useMemo(() => accessData?.users.find((u) => (u.id as string) === userId), [accessData, userId]);

  const userName = useMemo(() => {
    if (!user) return "";
    const name = [user.givenName, user.familyName].filter(Boolean).join(" ");
    return name || user.email;
  }, [user]);

  const userEmail = user?.email ?? "";

  const isTenantAdmin = useMemo(
    () =>
      accessData?.roles.some((r) => (r.user as string) === userId && (r.role as string) === "tenant_admin") ?? false,
    [accessData, userId],
  );

  const sitePermissions: SitePermission[] = useMemo(() => {
    if (!accessData) return [];
    const perms: SitePermission[] = [];
    for (const r of accessData.roles) {
      if ((r.user as string) !== userId) continue;
      const role = r.role as string;
      if (role.startsWith("site_manager:")) {
        perms.push({ siteId: role.substring("site_manager:".length), role: "manager" });
      } else if (role.startsWith("site_user:")) {
        perms.push({ siteId: role.substring("site_user:".length), role: "viewer" });
      }
    }
    return perms;
  }, [accessData, userId]);

  const cameraPermissions: CameraPermission[] = useMemo(() => {
    if (!accessData) return [];
    return accessData.cameras
      .filter((c) => (c.user as string) === userId)
      .map((c) => ({
        cameraId: c.camera,
        role: c.role === "camera_manager" ? ("manager" as const) : ("viewer" as const),
      }));
  }, [accessData, userId]);

  const siteList: SiteAccessListSite[] = useMemo(() => {
    if (!sites) return [];
    return sites.map((s) => {
      const siteId = String(s.siteId);
      const firstCamera = cameras?.find((c) => c.site.id === siteId);
      return {
        id: siteId,
        name: s.siteName,
        snapshotUrl: firstCamera?.snapshot?.url ?? null,
      };
    });
  }, [sites, cameras]);

  const cameraList: CameraAccessListCamera[] = useMemo(() => {
    if (!cameras || !siteNames) return [];
    const list = cameras.map((c) => {
      const cameraKey = `${c.key.asset}@${c.key.id}`;
      return {
        id: `${c.site.id}/${cameraKey}`,
        name: normalizeCameraNickname(c.nickname) || c.name?.trim() || formatCameraLabel(cameraKey),
        siteId: c.site.id,
        siteName: siteNames.get(c.site.id) ?? c.site.id,
        snapshotUrl: c.snapshot?.url ?? null,
        legacy: isLegacyCamera(`${c.site.id}/${cameraKey}`),
      };
    });

    const legacyGrantIds = new Set(
      cameraPermissions.map((p) => p.cameraId).filter((cameraId) => isLegacyCamera(cameraId)),
    );

    return withLegacyCameraFallbacks(list, legacyGrantIds, siteNames);
  }, [cameras, siteNames, cameraPermissions]);

  const isLoading = sitesLoading || camerasLoading || accessLoading;

  useLayoutEffect(() => {
    if (!permissionsLoading && !permissionsError && !isCurrentUserAdmin) {
      router.replace("/forbidden");
    }
  }, [permissionsLoading, permissionsError, isCurrentUserAdmin, router]);

  const navigateBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/settings/users-and-permissions");
    }
  }, [router]);

  const handleSubmit = useCallback(
    (data: EditUserFormData) => {
      const roles: Role[] = [];
      if (data.tenantAdmin) roles.push("tenant_admin");
      for (const [siteId, role] of data.sites) {
        roles.push(role === "manager" ? `site_manager:${siteId}` : `site_user:${siteId}`);
      }

      const cameraMutationPermissions = [...data.cameras.entries()].map(([cameraId, role]) => ({
        camera: cameraId,
        role: role === "manager" ? ("camera_manager" as const) : ("camera_viewer" as const),
      }));

      setUserPermissions.mutate(
        { user: userId, roles, cameraPermissions: cameraMutationPermissions },
        {
          onSuccess: () => {
            showSuccessToast({ message: "Permissions updated" });
            navigateBack();
          },
          onError: () => {
            showErrorToast({ message: "Failed to update permissions" });
          },
        },
      );
    },
    [setUserPermissions, userId, navigateBack],
  );

  const handleCancel = navigateBack;

  const handleRemoveAll = useCallback(async () => {
    if (selfGuard()) return;
    const confirmed = await universalConfirm({
      title: "Remove all access",
      message: "This will remove all roles and camera permissions for this user. This action cannot be undone.",
      confirmLabel: "Remove All",
    });
    if (!confirmed) return;
    revokeUserAccess.mutate(
      { user: userId },
      {
        onSuccess: () => {
          showSuccessToast({ message: "All access removed" });
          navigateBack();
        },
        onError: () => {
          showErrorToast({ message: "Failed to remove access" });
        },
      },
    );
  }, [revokeUserAccess, userId, navigateBack, selfGuard]);

  return {
    userName,
    userEmail,
    isTenantAdmin,
    isSelf,
    siteList,
    cameraList,
    sitePermissions,
    cameraPermissions,
    isLoading,
    isPending: setUserPermissions.isPending || revokeUserAccess.isPending,
    handleSubmit,
    handleCancel,
    handleRemoveAll,
  };
}
